import { Toggle, type ToggleEntry } from '@warp-ds/react/components/toggle';
import type { ReactNode } from 'react';

import type { ApiQueryParams, CheckboxMap, Facet } from '@/interfaces';

export interface CheckboxStateProps {
    id: string;
    label: string;
    facetList: Facet[];
    displayValues: Record<string, string | ReactNode>;
    checkboxValues: CheckboxMap;
    checkBoxClickMapper?: (checkBoxValue: string) => Record<string, unknown>;
    hideLabel?: boolean;
}

export interface CheckboxDispatchProps {
    checkBoxClick: (checkBoxValue: string | Record<string, unknown>) => void;
    fetchResults: (queryParams?: ApiQueryParams) => void;
}

type CheckboxProps = CheckboxDispatchProps & CheckboxStateProps;

export const CheckboxFilter = ({
    label,
    facetList,
    displayValues,
    checkboxValues,
    checkBoxClickMapper,
    checkBoxClick,
    fetchResults,
    hideLabel = false,
}: CheckboxProps) => {
    const onChange = (entry: ToggleEntry): void => {
        if (checkBoxClickMapper) {
            checkBoxClick(checkBoxClickMapper(entry.value as string));
        } else {
            checkBoxClick(entry.value as string);
        }
        fetchResults();
    };

    const createDisplayValue = (facet: Facet): string | ReactNode => {
        const displayValue = displayValues[facet.value];

        if (typeof displayValue === 'string') {
            return `${displayValue}${facet.count !== undefined && facet.count !== null ? ` (${facet.count})` : ''}`;
        }
        return (
            <div>
                {displayValues[facet.value]}
                {facet.count !== undefined && facet.count !== null && <span>({facet.count})</span>}
            </div>
        );
    };

    const options: ToggleEntry[] = facetList
        .filter((facet: Facet) => Boolean(displayValues[facet.value]))
        .map((facet) => ({ label: createDisplayValue(facet), value: facet.value }));

    const selected: ToggleEntry[] = options.filter((option) => Boolean(checkboxValues[option.value as string]));

    return (
        <Toggle
            type="checkbox"
            title={hideLabel ? undefined : label}
            options={options}
            selected={selected}
            onChange={onChange}
            className="space-y-8"
        />
    );
};
